export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

type TimeZoneSource = {
  timezone?: string | null;
  business?: { timezone?: string | null } | null;
};

function firstIanaTimeZone(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

/** IANA zone for staff booking clocks (work schedule / operating hours). */
export function resolveBusinessTimeZone(business?: TimeZoneSource | null): string {
  // getBusiness() returns { business: { timezone }, ... }; some callers store that wrapper.
  return (
    firstIanaTimeZone(business?.timezone, business?.business?.timezone) || getBrowserTimeZone()
  );
}

/**
 * Convert a wall-clock date + HH:mm in `timeZone` to a UTC ISO string.
 */
export function zonedLocalToUtcIso(ymd: string, timeHHmm: string, timeZone: string): string {
  const [year, month, day] = ymd.split('-').map(Number);
  const [hour, minute] = timeHHmm.split(':').map(Number);
  const h = Number.isFinite(hour) ? hour : 0;
  const min = Number.isFinite(minute) ? minute : 0;
  const utcGuess = Date.UTC(year, month - 1, day, h, min, 0);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date(utcGuess));
    const num = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const asUtc = Date.UTC(num('year'), num('month') - 1, num('day'), num('hour'), num('minute'), num('second'));
    return new Date(utcGuess - (asUtc - utcGuess)).toISOString();
  } catch {
    return new Date(utcGuess).toISOString();
  }
}

function minutesToHHmm(totalMinutes: number): string | null {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0 || totalMinutes >= 24 * 60) return null;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/** Convert a wall-clock HH:mm in `fromTimeZone` to minutes-from-midnight in `toTimeZone`. */
export function clockMinutesInTimeZone(
  ymd: string,
  timeHHmm: string,
  fromTimeZone: string,
  toTimeZone: string
): number {
  const [hourRaw, minuteRaw] = timeHHmm.split(':').map(Number);
  const fallback = (Number.isFinite(hourRaw) ? hourRaw : 0) * 60 + (Number.isFinite(minuteRaw) ? minuteRaw : 0);
  if (!fromTimeZone || !toTimeZone || fromTimeZone === toTimeZone) return fallback;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: toTimeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date(zonedLocalToUtcIso(ymd, timeHHmm, fromTimeZone)));
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value);
    return (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0);
  } catch {
    return fallback;
  }
}

/** Shift a business-local hours range into the viewer's timezone for the same calendar date. */
export function convertHoursRangeToTimeZone(
  ymd: string,
  range: { startMinutes: number; endMinutes: number },
  fromTimeZone: string,
  toTimeZone: string
): { startMinutes: number; endMinutes: number } {
  if (!fromTimeZone || !toTimeZone || fromTimeZone === toTimeZone) return range;
  const startClock = minutesToHHmm(range.startMinutes);
  const endClock = minutesToHHmm(range.endMinutes);
  if (!startClock || !endClock) return range;
  return {
    startMinutes: clockMinutesInTimeZone(ymd, startClock, fromTimeZone, toTimeZone),
    endMinutes: clockMinutesInTimeZone(ymd, endClock, fromTimeZone, toTimeZone)
  };
}

/**
 * Format a Date as YYYY-MM-DD in the *browser's local timezone*.
 * Do NOT use date.toISOString().split('T')[0] for date-only UI/API parameters,
 * because that returns the UTC date and can shift the day for non-UTC users.
 */
export function formatLocalYmd(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Convert a local (browser) date + "HH:mm" into a UTC ISO string (with trailing Z).
 * This represents the intended instant for the customer's local selection.
 */
export function localDateAndTimeToUtcIso(date: Date, timeHHmm: string): string {
  const [h, m] = timeHHmm.split(':').map(Number);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    Number.isFinite(h) ? h : 0,
    Number.isFinite(m) ? m : 0,
    0,
    0
  ).toISOString();
}


